import React from 'react';
import { Box, Center, Flex, Text, useColorModeValue, useDisclosure, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useReduxDispatch } from '../../../app/store';
import { SwipeableSEHomeCard } from '../../../common/components/SwipeableSEHomeCard';
import { getOrderedWeekdays, WEEKDAYS_TO_API_VALUES } from '../../../common/constants';
import { useRegionalConfig } from '../../../common/hooks/use-regional-config';
import { DeleteIcon, EditIcon } from '../../../styles/custom-icons';
import { formatScheduleTime } from '../../../utils/common/time-utils';
import { selectEvChargerData } from '../dashboard/evChargerSlice';
import { Schedule } from '../types';
import { deleteEvChargerSchedule, selectEvChargerSchedule } from './schedulerSlice';

type Props = {
  groupId: string;
  onEdit: (groupId: string) => void;
  isActionsDisabled?: boolean;
};

/* istanbul ignore next -- @preserve */
export function ScheduleCard({ groupId, onEdit, isActionsDisabled = false }: Props) {
  const dispatch = useReduxDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const { siteDeviceId } = useSelector(selectEvChargerData);
  const schedule: Schedule | null = useSelector(selectEvChargerSchedule(groupId));
  const toast = useToast();
  const textColor = useColorModeValue('customGrey.700', 'customGrey.400');
  const { weekStartsOn } = useRegionalConfig();
  const orderedWeekdays = getOrderedWeekdays(weekStartsOn);

  async function handleDeleteSchedule() {
    if (isActionsDisabled) return;

    const resultAction = await dispatch(deleteEvChargerSchedule({ siteDeviceId, groupId }));
    if (deleteEvChargerSchedule.rejected.match(resultAction)) {
      Sentry.captureException(resultAction.error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'ScheduleCard',
          method: 'handleDeleteSchedule',
        },
      });
      toast({
        title: t('Common.something went wrong'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
    }
  }

  if (!schedule) return null;

  const isNextDay = schedule.startTime && schedule.endTime ? schedule.endTime < schedule.startTime : false;

  return (
    <>
      <SwipeableSEHomeCard
        data-testid={`schedule-summary-${groupId}`}
        width="100%"
        mb={3}
        borderTopLeftRadius={5}
        swipeHintKey="schedules"
        hintOnTap
        primaryButtonProps={{
          onClick: () => {
            if (isActionsDisabled) return;
            onOpen();
          },
          'data-testid': `delete-schedule-button-${groupId}`,
          'aria-disabled': isActionsDisabled,
          pointerEvents: isActionsDisabled ? 'none' : 'auto',
          children: <DeleteIcon w="2rem" h="2rem" color={isActionsDisabled ? 'customGrey.400' : undefined} />,
        }}
        secondaryButtonProps={{
          onClick: () => {
            if (isActionsDisabled) return;
            onEdit(groupId);
          },
          'data-testid': `edit-schedule-button-${groupId}`,
          'aria-disabled': isActionsDisabled,
          pointerEvents: isActionsDisabled ? 'none' : 'auto',
          children: <EditIcon w="2rem" h="2rem" color={isActionsDisabled ? 'customGrey.400' : undefined} />,
        }}
      >
        <Box>{schedule.name}</Box>
        <Text data-testid={`schedule-${groupId}-turn-on-time`} fontSize="sm" color={textColor}>
          {t('Devices.turn on')}: {formatScheduleTime(schedule.startTime)}
        </Text>
        <Text data-testid={`schedule-${groupId}-turn-off-time`} fontSize="sm" color={textColor}>
          {t('Devices.turn off')}: {formatScheduleTime(schedule.endTime)} {isNextDay ? ' Next Day' : null}
        </Text>

        <Flex data-testid={`schedule-${groupId}-days-of-week`} mt={2} gap={1}>
          {orderedWeekdays.map((weekday) => {
            const weekdayValue = WEEKDAYS_TO_API_VALUES[weekday];
            const isActive = schedule.daysOfWeek.includes(weekdayValue);
            return (
              <Center
                key={weekday}
                w="24px"
                h="24px"
                borderRadius="full"
                bg={isActive ? 'primaryBranding.200' : 'transparent'}
                border={isActive ? 'none' : '1px solid'}
                borderColor={isActive ? undefined : 'customGrey.400'}
              >
                <Text fontSize="xs" fontWeight="bold" color={isActive ? 'white' : textColor}>
                  {t(`Common.${weekday.toLowerCase()}`)[0]}
                </Text>
              </Center>
            );
          })}
        </Flex>
      </SwipeableSEHomeCard>

      <AlertDialogModal
        isOpen={isOpen}
        onClose={onClose}
        header={t('EVSE.delete schedule?')}
        subHeader={t('EVSE.delete schedule confirmation')}
        confirmButtonName={t('Common.delete')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={handleDeleteSchedule}
      />
    </>
  );
}
